import { isIEventrActivity, recursiveFindValue } from "@eventr/common";
import { memo, useMemo } from "react";
import { ColoursObj, Icon, TEventrIcons, TicketIcon } from "@eventr/common-storybook";
import { useShallow } from "zustand/react/shallow";
import { useBoundStore } from "../state/bound.store";
import { ActivityCard } from "./componentCards/activityCard";
import { FlightCard } from "./componentCards/flightCard";
import HireCarCard from "./componentCards/hireCarCard";
import HotelCard from "./componentCards/hotelCard";
import ParkingCard from "./componentCards/parkingCard";
import { TodoCard } from "./componentCards/todoCard";
import TransportCard from "./componentCards/transportCard";
import { ArrayFinder } from "./components/arrayFinder";
import { Container } from "./components/container";
import { Counter } from "./components/counter";
import { EditArrowIcon } from "./components/editArrowIcon";
import { EditButton } from "./components/editButton";
import { ExpandableCard } from "./components/expandableCard";
import { FileList } from "./components/fileList";
import { FlightPassengersBody } from "./components/flightPassengersBody";
import { HireCarsPassengersBody } from "./components/hireCarsPassengersBody";
import { Iterator } from "./components/iterator";
import { ListHeader } from "./components/listHeader";
import { ParkingCarBody } from "./components/parkingCarBody";
import { PushPathContainer } from "./components/pushPathContainer";
import { RoomBody } from "./components/roomBody";
import { ShouldRender } from "./components/shouldRender";
import { SimpleCard } from "./components/simpleCard";
import { SimpleFromToTimes } from "./components/simpleFromToTimes";
import { SpaceBetween } from "./components/spaceBetween";
import { Spacer } from "./components/spacer";
import { SubItemEditButton } from "./components/subPageEditButton";
import Text from "./components/text";
import { TransportPassengersBody } from "./components/transportPassengersBody";
import { UsersList } from "./components/usersList";
import { useScreenTypeContext } from "./state/screenTypeProvider";
import { useSectionV2Context } from "./state/sectionProvider";

interface IComponentFinderProps {
  id?: string;
  data?: any;
  componentKey?: string;
  renderOptions?: any;
  parentData?: any;
  isInPast?: boolean;
}

/**
 * Memoised because it is the per-item leaf of every itinerary list: a large imported section
 * renders one of these per card, and without it every card re-rendered on each keystroke in the
 * section search box.
 */
export const ComponentFinder = memo(function ComponentFinder({
  data,
  componentKey,
  renderOptions,
  parentData,
  isInPast = false,
}: IComponentFinderProps) {
  const { screenType } = useScreenTypeContext();
  const { past, categories } = useBoundStore(useShallow((s) => ({ past: s.past, categories: s.orgConfig.categories })));
  const { sectionData } = useSectionV2Context();
  // Memoised so the card below sees a stable `data` identity — a fresh object here would defeat
  // that card's own memo on every render.
  const sanitisedData = useMemo(
    () =>
      data?.categories
        ? {
            ...data,
            categories: data.categories.filter((catId: string) =>
              categories?.some((existingCatId) => existingCatId.pub_id === catId),
            ),
          }
        : data,
    [data, categories],
  );

  const pastToUse = past ? past : isInPast;

  if (!renderOptions?.visible || renderOptions?.visible.includes(screenType))
    if (!renderOptions?.hidden || !renderOptions?.hidden.includes(screenType))
      switch (componentKey) {
        case "text":
          return (
            <Text
              id={`text.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
              past={pastToUse}
            />
          );

        case "spaceBetween":
          return <SpaceBetween data={sanitisedData} renderOptions={renderOptions} past={pastToUse} />;
        case "usersList":
          return (
            <UsersList
              id={`usersList.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
              past={pastToUse}
            />
          );
        case "fileList":
          return (
            <FileList
              id={`fileList.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );
        case "iterator":
          return <Iterator data={sanitisedData} renderOptions={renderOptions} past={pastToUse} />;
        case "simpleFromToTimes":
          return <SimpleFromToTimes data={sanitisedData} renderOptions={renderOptions} />;
        case "expandableCard":
          return (
            <ExpandableCard
              data={sanitisedData}
              renderOptions={renderOptions}
              parentData={parentData}
              past={pastToUse}
            />
          );
        case "simpleCard":
          return (
            <SimpleCard data={sanitisedData} renderOptions={renderOptions} parentData={parentData} past={pastToUse} />
          );

        case "spacer":
          return <Spacer data={sanitisedData} renderOptions={renderOptions} />;
        case "icon":
          return renderOptions?.ticket ? (
            <TicketIcon
              name={renderOptions.icon as TEventrIcons}
              iconColour={
                pastToUse
                  ? ColoursObj.shades.shade2
                  : renderOptions.colour
                    ? (recursiveFindValue(renderOptions.colour, ColoursObj) ?? ColoursObj.core.primary)
                    : ColoursObj.core.primary
              }
            />
          ) : (
            <Icon
              width={renderOptions.width}
              height={renderOptions.height}
              name={renderOptions.icon as TEventrIcons}
              iconColour={
                pastToUse
                  ? renderOptions.bubble
                    ? ColoursObj.backgrounds.secondary
                    : ColoursObj.shades.shade2
                  : renderOptions.colour
                    ? (recursiveFindValue(renderOptions.colour, ColoursObj) ?? ColoursObj.core.primary)
                    : ColoursObj.core.primary
              }
              bubble={renderOptions.bubble}
              bubbleColour={
                pastToUse
                  ? ColoursObj.shades.shade2
                  : recursiveFindValue(renderOptions.bubbleColour ?? "core.primary", ColoursObj)
              }
            />
          );
        case "container":
          return (
            <Container data={sanitisedData} renderOptions={renderOptions} parentData={parentData} past={pastToUse} />
          );
        case "arrayFinder":
          return <ArrayFinder data={sanitisedData} renderOptions={renderOptions} past={pastToUse} />;
        case "shouldRender":
          return (
            <ShouldRender data={sanitisedData} renderOptions={renderOptions} parentData={parentData} past={pastToUse} />
          );
        case "editButton":
          return (
            <EditButton
              id={`editButton.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );

        case "editArrowIcon":
          return <EditArrowIcon data={sanitisedData} renderOptions={renderOptions} />;
        case "counter":
          return (
            <Counter
              id={`counter.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );
        case "subPageEditButton":
          return <SubItemEditButton data={sanitisedData} renderOptions={renderOptions} />;
        case "pushPathContainer":
          return (
            <PushPathContainer
              data={sanitisedData}
              renderOptions={renderOptions}
              parentData={parentData}
              past={pastToUse}
            />
          );
        case "listHeader":
          return (
            <ListHeader
              id={`listHeader.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );
        case "roomBody":
          return (
            <RoomBody
              id={`roomBody.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );

        case "flightPassengersBody":
          return (
            <FlightPassengersBody
              id={`flightPassengersBody.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );
        case "transportPassengersBody":
          return (
            <TransportPassengersBody
              id={`transportPassengersBody.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );
        case "parkingCarBody":
          return (
            <ParkingCarBody
              id={`parkingCarBody.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );
        case "hireCarsPassengersBody":
          return (
            <HireCarsPassengersBody
              id={`hireCarsPassengersBody.${sectionData?.sectionName.toLowerCase()}`}
              data={sanitisedData}
              renderOptions={renderOptions}
            />
          );

        case "activitiesCard":
          // Route todo-type activities to TodoCard, since ActivityCard does not
          // handle the todo structure (tasks, checkboxes, etc.).
          if (isIEventrActivity(sanitisedData) && sanitisedData.type === "todo") {
            return <TodoCard id="todo" todo={sanitisedData} />;
          }
          return (
            <ActivityCard id={`${sectionData?.sectionName.toLowerCase()}`} data={sanitisedData} past={pastToUse} />
          );
        case "flightCard":
          return <FlightCard id={`${sectionData?.sectionName.toLowerCase()}`} data={sanitisedData} past={pastToUse} />;
        case "hotelCard":
          return <HotelCard id={`${sectionData?.sectionName.toLowerCase()}`} data={sanitisedData} past={pastToUse} />;
        case "parkingCard":
          return <ParkingCard id={`${sectionData?.sectionName.toLowerCase()}`} data={sanitisedData} past={pastToUse} />;
        case "transportCard":
          return (
            <TransportCard id={`${sectionData?.sectionName.toLowerCase()}`} data={sanitisedData} past={pastToUse} />
          );
        case "hireCarCard":
          return <HireCarCard id={`${sectionData?.sectionName.toLowerCase()}`} data={sanitisedData} past={pastToUse} />;
        default:
          return null;
      }

  return null;
});
